import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { UNSAFE_UpdateOnChange as SpotlightUpdateOnChange } from '@atlaskit/spotlight/update-on-change';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { Text } from '@atlaskit/primitives/compiled';

import { PlacementFetchProvider } from '@atlassian/confluence-post-office-provider/entry-points/preload-placement';
import { useMessageData } from '@atlassian/post-office-headless-api/hooks/useMessageData';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { fg } from '@confluence/feature-gating';

import spotlightMediaPlaceholder from './assets/spotlight-media-placeholder.svg';

const MESSAGE_TEMPLATE_ID = 'confluence-native-tabs-toolbar-spotlight';
const PLACEMENT_ID = 'headless';

const styles = cssMap({
	media: {
		display: 'block',
		height: '135px',
		width: '295px',
	},
});

const messages = defineMessages({
	body: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.body',
		defaultMessage:
			'Structure related content into easy-to-scan tabs, so readers can quickly find what they need.',
		description: 'Body text for the Native Tabs toolbar spotlight.',
	},
	close: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.close',
		defaultMessage: 'Close',
		description:
			'Accessible label for the close button that dismisses the Native Tabs toolbar spotlight.',
	},
	done: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.done',
		defaultMessage: 'Done',
		description: 'Button text that dismisses the Native Tabs toolbar spotlight.',
	},
	title: {
		id: 'editor-plugin-native-tabs-toolbar.spotlight.title',
		defaultMessage: 'Organize your content with tabs',
		description: 'Title for the Native Tabs toolbar spotlight.',
	},
});

type TabsButtonSpotlightProps = {
	children: React.ReactElement;
};

const TabsButtonSpotlightMessage = ({ children }: TabsButtonSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { PopoverContent } = useChoreographerAssets();
	const { message, PostOfficeMessage, dismissMessage } = useMessageData(
		{
			placementId: PLACEMENT_ID,
			messageTemplateId: MESSAGE_TEMPLATE_ID,
		},
		{
			dataOutsideWrapper: true,
		},
	);

	const handleDismiss = useCallback(() => {
		if (message) {
			dismissMessage(message.messageInstanceId);
		}
	}, [dismissMessage, message]);
	const spotlight = (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<SpotlightUpdateOnChange />
			{!!message && (
				<PopoverContent dismiss={handleDismiss} isVisible offset={[0, 8]} placement="bottom-center">
					<SpotlightCard testId="native-tabs-toolbar-spotlight-card">
						<SpotlightHeader>
							<SpotlightHeadline>{formatMessage(messages.title)}</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={handleDismiss} autoFocus={false} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<img css={styles.media} alt="" src={spotlightMediaPlaceholder} />
						</SpotlightMedia>
						<SpotlightBody>
							<Text>{formatMessage(messages.body)}</Text>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleDismiss}>
									{formatMessage(messages.done)}
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			)}
		</PopoverProvider>
	);

	if (!PostOfficeMessage || !message) {
		return (
			<PopoverProvider>
				<PopoverTarget>{children}</PopoverTarget>
			</PopoverProvider>
		);
	}

	return <PostOfficeMessage>{() => spotlight}</PostOfficeMessage>;
};
export const TabsButtonSpotlight = ({ children }: TabsButtonSpotlightProps): React.JSX.Element => {
	if (!fg('post-office_enable_conf-tabs-toolbar-spotlight')) {
		return children;
	}

	return (
		<PlacementFetchProvider>
			<TabsButtonSpotlightMessage>{children}</TabsButtonSpotlightMessage>
		</PlacementFetchProvider>
	);
};
